// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import VideoAsset, { VideoAssetsStyles } from "@conklin/App/Components/Media/VideoAsset";
import { SwiperStyle } from "@conklin/App/Components/XngCarousel";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import IconLargeArrowLeft from "@conklin/XDL/Icons/IconLargeArrowLeft";
import IconLargeArrowRight from "@conklin/XDL/Icons/IconLargeArrowRight";
import IconPaginationArrowDownSmall from "@conklin/XDL/Icons/IconPaginationArrowDownSmall";
import IconPaginationArrowUpSmall from "@conklin/XDL/Icons/IconPaginationArrowUpSmall";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { DocumentModel, ImageModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    scrollingBehavior?: string;
    numberOfVisibleThumbnails?: number;
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
    // XNG-Changes
    videos?: DocumentModel[];
}

const mapStateToProps = (state: ApplicationState, ownProps: HasProductContext) => ({
    productSettings: getSettingsCollection(state).productSettings,
    // XNG-Change
    isTabletScreen: (state as CustomAppState).xCommon?.isTabletScreen,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    verticalPrevArrowGridItem?: GridItemProps;
    verticalCarouselGridItem?: GridItemProps;
    verticalNextArrowGridItem?: GridItemProps;
    // XNG-Change
    slidesContainer?: InjectableCss;
    prevFadeOut?: InjectableCss;
    nextFadeOut?: InjectableCss;
    videoThumb?: LazyImageProps;
    selectedVideoThumb?: LazyImageProps;
    carousel?: SwiperStyle;
    videoStyles?: VideoAssetsStyles;
}

const getImageCss = (borderColor: string) => css`
    width: 100%;
    height: 100%;
    max-width: 64px;
    max-height: 64px;
    border: 1px solid ${getColor(borderColor)};
    cursor: pointer;
    justify-content: flex-start;
    img {
        max-width: 64px;
        max-height: 64px;
        padding: 3px;
    }
`;

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
        `,
    },
    verticalPrevArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: { width: 10 },
    verticalCarouselGridItem: { width: 12 },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            padding: 10px 3px 0;
            display: flex;
            justify-content: center;
        `,
    },
    image: {
        css: getImageCss("common.border"),
    },
    selectedImage: {
        css: getImageCss("text.main"),
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
        `,
    },
    verticalNextArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
};

export const CarouselSlidesContainer = styled.div<
    InjectableCss & { isVertical: boolean; numberOfVisibleThumbnails: number }
>`
    display: flex;
    flex-direction: ${props => (props.isVertical ? "column" : "row")};
    align-items: ${props => (props.isVertical ? "center" : "initial")};
    ${props => (props.isVertical ? `height: ${props.numberOfVisibleThumbnails * 74}px;` : "")}
    ${injectCss};
`;

export const CarouselSlide = styled.div<InjectableCss & { numberOfVisibleThumbnails: number }>`
    flex: 0 0 ${props => 100 / props.numberOfVisibleThumbnails}%;
    padding: 0;
    position: relative;
    ${injectCss};
`;

const ProductImageCarousel = ({
    productSettings,
    productContext,
    scrollingBehavior,
    numberOfVisibleThumbnails = 6,
    selectedIndex,
    onSelectImage,
    extendedStyles,
    // XNG-Change
    isTabletScreen,
    videos,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;

    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    // XNG-Changes
    const totalMediaCount = (product.images?.length || 0) + (videos?.length || 0);

    // XNG-Change
    const isVertical = !isTabletScreen;
    const [emblaRef, embla] = useEmblaCarousel({
        axis: isVertical ? "y" : "x",
        watchDrag: !!(canScrollPrev || canScrollNext),
    });

    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const updateVisibility = () => {
        if (!embla || !product.images) {
            return;
        }

        const canScroll = embla.canScrollPrev() || embla.canScrollNext();
        if (!canScroll) {
            embla.slideNodes().forEach(slide => {
                slide.setAttribute("data-is-visible", "true");
            });
            return;
        }

        const viewport = embla.rootNode().getBoundingClientRect();
        const buffer = 5;

        embla.slideNodes().forEach((slide, index) => {
            const slideRect = slide.getBoundingClientRect();
            const isFullyVisible =
                slideRect.top >= viewport.top - buffer &&
                slideRect.bottom <= viewport.bottom + buffer &&
                slideRect.left >= viewport.left - buffer &&
                slideRect.right <= viewport.right + buffer;

            slide.setAttribute("data-is-visible", isFullyVisible ? "true" : "false");
        });
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        const handleUpdate = () => {
            setCanScroll();
            updateVisibility();
        };

        embla.on("init", handleUpdate);
        embla.on("select", handleUpdate);
        embla.on("reInit", handleUpdate);
        embla.on("settle", handleUpdate);

        handleUpdate();
    }, [embla]);

    // Xng-Changes: handle videos
    useEffect(() => {
        if (!embla || (!product?.images?.length && !videos?.length)) {
            return;
        }

        embla.reInit({
            align: totalMediaCount > numberOfVisibleThumbnails || isVertical ? "start" : "center",
            slidesToScroll: 1,
            axis: isVertical ? "y" : "x",
            containScroll: "trimSnaps",
        });
        updateVisibility();
    }, [embla, product, videos, scrollingBehavior, numberOfVisibleThumbnails, isTabletScreen]);

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.includes("?") ? "&thumb" : "?thumb");
        }
        return "";
    };

    // XNG-Changes: handle videos
    if (!product || ((!product.images || product.images.length === 0) && (!videos || videos.length === 0))) {
        return null;
    }
    return (
        // XNG-Change: add specific style when the carousel is vertical
        <GridContainer {...styles.mainContainer} {...{ isVertical }} key={product.id}>
            <GridItem {...(isVertical ? styles.verticalPrevArrowGridItem : styles.prevArrowGridItem)}>
                {/* XNG-Changes */}
                {totalMediaCount > numberOfVisibleThumbnails && canScrollPrev && (
                    <Button
                        {...styles.prevArrowButton}
                        onClick={() => embla && embla.scrollPrev()}
                        disabled={!canScrollPrev}
                        aria-label={translate("Previous")}
                        data-test-selector="prevBtn"
                    >
                        <ButtonIcon src={isVertical ? IconPaginationArrowUpSmall : IconLargeArrowLeft} />
                    </Button>
                )}
                {/* XNG-Changes */}
                {canScrollPrev && <StyledWrapper {...styles.prevFadeOut} />}
            </GridItem>
            <GridItem {...(isVertical ? styles.verticalCarouselGridItem : styles.carouselGridItem)}>
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <CarouselSlidesContainer
                            {...styles.slidesContainer}
                            isVertical={isVertical}
                            numberOfVisibleThumbnails={numberOfVisibleThumbnails}
                            data-test-selector="slides"
                            data-test-selector-isvertical={isVertical}
                        >
                            {product.images?.map((productImage, index) => (
                                <CarouselSlide
                                    key={productImage.id || `slide_${index}`}
                                    numberOfVisibleThumbnails={numberOfVisibleThumbnails}
                                    data-is-selected={index === selectedImageIndex}
                                    data-is-visible="false"
                                >
                                    <StyledWrapper {...styles.carouselSlideInner} data-test-selector="carousel_slide">
                                        <Clickable onClick={() => imageClickHandler(index)}>
                                            <LazyImage
                                                {...(index === selectedImageIndex
                                                    ? styles.selectedImage
                                                    : styles.image)}
                                                src={getProductImageThumbPath(productImage)}
                                                altText={productImage.imageAltText}
                                                width="64px"
                                                height="64px"
                                            />
                                        </Clickable>
                                    </StyledWrapper>
                                </CarouselSlide>
                            ))}
                            {/* XNG-Changes: added videos */}
                            {videos &&
                                videos.map((vid, index) => (
                                    <CarouselSlide
                                        key={vid.id || `slide_${(product.images?.length || 0) + index}`}
                                        numberOfVisibleThumbnails={numberOfVisibleThumbnails}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            data-test-selector="carousel_slide"
                                        >
                                            <Clickable
                                                onClick={() => imageClickHandler((product.images?.length || 0) + index)}
                                                aria-label="product video clickable"
                                            >
                                                <VideoAsset
                                                    id={vid.id}
                                                    name={vid.name}
                                                    url={vid.filePath}
                                                    type="Vimeo"
                                                    showThumbnail={true}
                                                    extendedStyles={{
                                                        ...styles.videoStyles,
                                                        thumb:
                                                            (product.images?.length || 0) + index === selectedImageIndex
                                                                ? styles.selectedImage
                                                                : styles.image,
                                                    }}
                                                />
                                            </Clickable>
                                        </StyledWrapper>
                                    </CarouselSlide>
                                ))}
                        </CarouselSlidesContainer>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            <GridItem {...(isVertical ? styles.verticalNextArrowGridItem : styles.nextArrowGridItem)}>
                {/* XNG-Changes */}
                {totalMediaCount > numberOfVisibleThumbnails && canScrollNext && (
                    <Button
                        {...styles.nextArrowButton}
                        onClick={() => embla && embla.scrollNext()}
                        disabled={!canScrollNext}
                        aria-label={translate("Next")}
                        data-test-selector="nextBtn"
                    >
                        <ButtonIcon src={isVertical ? IconPaginationArrowDownSmall : IconLargeArrowRight} />
                    </Button>
                )}
                {/* XNG-Changes */}
                {canScrollNext && <StyledWrapper {...styles.nextFadeOut} />}
            </GridItem>
        </GridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductImageCarousel));
